import { Dispatch, useState } from "react";
import { Alert, Button, Modal, Card, Badge } from "react-bootstrap";
import { FiCreditCard, FiDollarSign, FiCheck, FiArrowUp, FiCalendar, FiX } from "react-icons/fi";
import { PreviewResponse, updateSubscription } from "../../../api/payment";
import { useMutation, useQueryClient } from "react-query";
import SpinnerFullSection from "../../Other/SpinnerFullSection";

type Props = {
    preview?: PreviewResponse;
    show: boolean;
    onClose: () => void;
    openStripeSession: (sessionId: string) => Promise<void>;
    setLimitsExceededError: Dispatch<React.SetStateAction<boolean>>;
};
function UpgradeSubscription({
    preview,
    show,
    onClose,
    openStripeSession,
    setLimitsExceededError,
}: Props) {
    const [error, setError] = useState<string>();

    const onHide = () => {
        setError(undefined);
        onClose();
    };

    const queryClient = useQueryClient();
    const mutation = useMutation(updateSubscription, {
        onSuccess: async (data) => {
            if (data.sessionId) {
                await openStripeSession(data.sessionId);
            }
            queryClient.invalidateQueries("Subscription");
            queryClient.invalidateQueries(["Organizations"]);
            setError(undefined);
            onClose();
        },
        onError: (error: any) => {
            if (error?.response?.data?.name === "LimitsExceededError") {
                setLimitsExceededError(true);
                onHide();
                return;
            }
            setError("Unable to process payment.");
        },
    });

    const handleUpgrade = () => {
        if (!preview) {
            return;
        }
        mutation.mutateAsync({
            orgId: preview.ordId,
            subscriptionId: preview.subscriptionId,
            newPriceId: preview.newPriceId,
            proration_date: preview.proration_date,
        });
    };
    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-dark text-white border-secondary">
                <Modal.Title className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-25 p-2 rounded-circle me-3">
                        <FiArrowUp className="text-primary" size={20} />
                    </div>
                    <div>
                        <h5 className="mb-0 text-white">Subscription Upgrade</h5>
                        <small style={{color: '#ccc'}}>Confirm your plan changes</small>
                    </div>
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="p-0 bg-dark">
                {mutation.isLoading && <SpinnerFullSection />}

                <div className="p-4">
                    <Card className="bg-dark border-secondary mb-4">
                        <Card.Header className="bg-dark border-secondary">
                            <div className="d-flex align-items-center">
                                <FiDollarSign className="text-primary me-2" size={18} />
                                <h6 className="text-white mb-0">Billing Summary</h6>
                                <Badge bg="primary" className="ms-auto">
                                    {preview?.prorationItems.length || 0} Items
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="bg-dark p-0">
                            {preview?.prorationItems.map((item, index) => (
                                <div
                                    key={index}
                                    className="border-bottom border-secondary p-3"
                                >
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="flex-grow-1">
                                            <h6 className="text-white mb-1 fw-semibold">
                                                {item.description}
                                            </h6>
                                            <div className="d-flex align-items-center small" style={{ color: '#ccc' }}>
                                                <FiCalendar className="me-1" size={14} />
                                                {item.period.start} – {item.period.end}
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <div className="text-white fw-bold">
                                                ${Math.abs(item.amount).toFixed(2)}
                                            </div>
                                            <div className="small text-uppercase" style={{ color: '#ccc' }}>
                                                {item.currency}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>

                    <Card className="bg-primary bg-opacity-25 border-primary mb-4">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-50 p-2 rounded-circle me-3">
                                        <FiCheck className="text-white" size={16} />
                                    </div>
                                    <div>
                                        <h5 className="text-white mb-0 fw-bold">
                                            Total Due Today
                                        </h5>
                                        <small style={{ color: '#ccc' }}>
                                            Prorated amount for immediate billing
                                        </small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <h3 className="text-white fw-bold mb-0">
                                        ${preview?.total?.toFixed(2)}
                                    </h3>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {error && (
                        <Alert variant="danger" className="bg-danger bg-opacity-25 text-light border-danger">
                            <div className="d-flex align-items-start">
                                <FiX className="text-danger me-2 mt-1" size={16} />
                                <div>
                                    <strong>Payment Error</strong>
                                    <p className="mb-0 mt-1">{error}</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                </div>
            </Modal.Body>
            
            <Modal.Footer className="bg-dark border-secondary p-4">
                <div className="d-flex justify-content-between w-100">
                    <Button
                        variant="outline-secondary"
                        onClick={onHide}
                        className="d-flex align-items-center gap-2 px-4"
                        disabled={mutation.isLoading}
                    >
                        <FiX size={16} />
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpgrade}
                        className="d-flex align-items-center gap-2 px-4 fw-semibold"
                        disabled={mutation.isLoading}
                    >
                        {mutation.isLoading ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <FiCheck size={16} />
                                Confirm Upgrade
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default UpgradeSubscription;
